/**
 * SequenceChallenge Component
 * 
 * This component provides an interactive challenge where users must execute a predefined sequence of chess moves.
 * It manages game state, move validation, timing, hints, undo functionality, and recovery paths.
 * It integrates with ChessBoard for visual interaction and SequenceTracker for progress display.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { MoveSequence, SequenceStatus, ChessMove, PieceType } from '../../../types/chess';
import styles from './SequenceChallenge.module.css';

import ChessBoard from '../../chess/ChessBoard';
import SequenceTracker from '../SequenceTracker';
import TacticalContext, { TacticalContextProps } from '../TacticalContext';
import ProgressVisualizer from '../../common/ProgressVisualizer';
import Timer from '../../common/Timer';
import ChessService from '../../../services/chess/chessService';
import { Chess, Move as ChessJsMove, Square } from 'chess.js';
import { handleKeyboardShortcuts } from '../../../utils/keyboardNavigation';
import { announceToScreenReader } from '../../../utils/accessibilityHelpers';
import { getTopHints } from '../../../data/tacticalHints';

export interface SequenceChallengeResult {
  success: boolean;
  score: number;
  moves: string[];
  timeSpent: number;
}

export interface SequenceChallengeProps {
  sequence: MoveSequence;
  /** Optional difficulty level of the challenge (e.g., 1-5). May affect hints, scoring, or AI behavior. */
  difficulty?: number;
  timeLimit?: number;
  /** Whether to display visual hints (e.g., highlighting squares) for the next correct move. */
  showHints?: boolean;
  onComplete: (result: SequenceChallengeResult) => void;
  /** Enable keyboard navigation for the chess board */
  enableKeyboardNavigation?: boolean;
  /** Callback when keyboard shortcuts are used */
  onKeyboardShortcut?: (shortcut: string) => void;
}

/** Defines the squares to be highlighted for a hint. */
interface HintSquares {
  /** The starting square of the hinted move. */
  from?: Square;
  /** The destination square of the hinted move. */
  to?: Square;
}

// Helper function to map tactical themes to objective types
const mapTacticalThemeToObjectiveType = (
  theme?: string
): TacticalContextProps['objectiveType'] => {
  if (!theme) return 'position'; // Default if no theme is provided
  const lowerTheme = theme.toLowerCase();
  if (lowerTheme.includes('checkmate') || lowerTheme.includes('mate')) return 'checkmate';
  if (
    lowerTheme.includes('material') ||
    lowerTheme.includes('gain') ||
    lowerTheme.includes('fork') ||
    lowerTheme.includes('pin') ||
    lowerTheme.includes('skewer') ||
    lowerTheme.includes('capture')
  ) {
    return 'material';
  }
  // Default for other themes like 'opening', 'defense', 'positional advantage' etc.
  return 'position';
};

/**
 * The main component for handling chess sequence challenges.
 * It orchestrates the interaction between the chessboard, move validation, timing,
 * hints, undo, and completion logic based on the provided `MoveSequence`.
 */
const SequenceChallenge: React.FC<SequenceChallengeProps> = ({
  sequence,
  difficulty,
  timeLimit,
  showHints,
  onComplete,
  enableKeyboardNavigation = false,
  onKeyboardShortcut,
}) => {
  const [currentFen, setCurrentFen] = useState<string>(sequence.startingPosition);
  const [executedMoves, setExecutedMoves] = useState<string[]>([]);
  const [currentMoveIndex, setCurrentMoveIndex] = useState<number>(0);
  const [challengeStatus, setChallengeStatus] = useState<SequenceStatus>('in-progress');
  const [timeLeft, setTimeLeft] = useState<number | null>(timeLimit || null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [hintSquares, setHintSquares] = useState<HintSquares | null>(null);
  const [boardResetNonce, setBoardResetNonce] = useState<number>(0);
  const [wrongMoveCount, setWrongMoveCount] = useState<number>(0);
  const [undoCount, setUndoCount] = useState<number>(0);
  const [hintCount, setHintCount] = useState<number>(0);
  const [autoMoveCount, setAutoMoveCount] = useState<number>(0);

  // Undo functionality state
  const [boardHistory, setBoardHistory] = useState<string[]>([sequence.startingPosition]);
  const [moveHistory, setMoveHistory] = useState<string[]>([]);

  // Recovery path state
  const [showRecoveryOptions, setShowRecoveryOptions] = useState<boolean>(false);
  const [recoveryHintSquares, setRecoveryHintSquares] = useState<HintSquares | null>(null);

  // Keyboard navigation ref
  const containerRef = useRef<HTMLDivElement>(null);

  const chessService = useMemo(() => {
    const chessInstance = new Chess(sequence.startingPosition);
    return ChessService.create(chessInstance);
  }, [sequence.startingPosition]);

  const tacticalHintText = useMemo(() => {
    if (!sequence.tacticalTheme) return undefined;
    const hints = getTopHints(sequence.tacticalTheme, 3);
    if (!hints.length) return undefined;

    const unlockedHints = showHints
      ? Math.min(hints.length, Math.max(1, currentMoveIndex + 1))
      : Math.min(hints.length, Math.floor(currentMoveIndex / 2));

    if (unlockedHints <= 0) return undefined;
    return hints
      .slice(0, unlockedHints)
      .map((hint, index) => `${index + 1}. ${hint.text}`)
      .join(' ');
  }, [sequence.tacticalTheme, showHints, currentMoveIndex]);

  const calculateFinalScore = useCallback((
    overrides?: Partial<{
      wrongMoveCount: number;
      undoCount: number;
      hintCount: number;
      autoMoveCount: number;
    }>
  ): number => {
    const wrong = overrides?.wrongMoveCount ?? wrongMoveCount;
    const undos = overrides?.undoCount ?? undoCount;
    const hints = overrides?.hintCount ?? hintCount;
    const autoMoves = overrides?.autoMoveCount ?? autoMoveCount;

    let score = 100;
    score -= wrong * 12;
    score -= undos * 6;
    score -= hints * 10;
    score -= autoMoves * 20;

    return Math.max(0, Math.min(100, Math.round(score)));
  }, [wrongMoveCount, undoCount, hintCount, autoMoveCount]);

  /**
   * Effect to reset the challenge state when the sequence prop changes.
   * This ensures the component is correctly initialized for a new sequence.
   */
  useEffect(() => {
    setCurrentFen(sequence.startingPosition);
    setExecutedMoves([]);
    setCurrentMoveIndex(0);
    setChallengeStatus('in-progress');
    setTimeLeft(timeLimit || null);
    setErrorFeedback(null);
    setStartTime(Date.now());
    setHintSquares(null);
    setBoardResetNonce(0);
    setWrongMoveCount(0);
    setUndoCount(0);
    setHintCount(0);
    setAutoMoveCount(0);
    
    // Reset undo-related state
    setBoardHistory([sequence.startingPosition]);
    setMoveHistory([]);
    
    // Reset recovery path state
    setShowRecoveryOptions(false);
    setRecoveryHintSquares(null);
    
    chessService.resetBoard(sequence.startingPosition);
  }, [sequence, timeLimit, chessService]);

  /**
   * Effect to manage and display visual hints for the current move.
   * If `showHints` is true and the challenge is in progress,
   * it attempts to determine and set the `from` and `to` squares for the next expected move.
   */
  useEffect(() => {
    if (showHints && challengeStatus === 'in-progress' && currentMoveIndex < sequence.moves.length) {
      const expectedMoveSAN = sequence.moves[currentMoveIndex];
      const tempGameForHint = new Chess(currentFen);
      try {
        const moveDetails = tempGameForHint.move(expectedMoveSAN);
        if (moveDetails) {
          setHintSquares({ from: moveDetails.from, to: moveDetails.to });
        } else {
          setHintSquares(null);
        }
      } catch (e) {
        console.error("Error parsing hint SAN:", expectedMoveSAN, e);
        setHintSquares(null);
      }
    } else {
      setHintSquares(null);
    }
  }, [showHints, challengeStatus, currentMoveIndex, sequence.moves, currentFen]);

  /**
   * Updates the component's state after a move, including the FEN, executed moves,
   * current move index, and importantly, the history for undo functionality.
   * @param newFen The new FEN string representing the board state after the move.
   * @param newExecutedMoves The updated array of executed move strings (SAN).
   * @param newMoveIndex The new index pointing to the current move in the sequence.
   */
  const updateStateWithHistory = useCallback((
    newFen: string, 
    newExecutedMoves: string[], 
    newMoveIndex: number
  ) => {
    // Update current state
    setCurrentFen(newFen);
    setExecutedMoves(newExecutedMoves);
    setCurrentMoveIndex(newMoveIndex);
    
    // Update history for undo functionality
    const newBoardHistory = [...boardHistory];
    const newMoveHistory = [...moveHistory];
    
    // Ensure we have the right number of entries
    while (newBoardHistory.length <= newMoveIndex) {
      newBoardHistory.push(newFen);
    }
    while (newMoveHistory.length < newMoveIndex) {
      newMoveHistory.push(newExecutedMoves[newMoveHistory.length] || '');
    }
    
    // Update the current position
    newBoardHistory[newMoveIndex] = newFen;
    if (newMoveIndex > 0) {
      newMoveHistory[newMoveIndex - 1] = newExecutedMoves[newMoveIndex - 1] || '';
    }
    
    setBoardHistory(newBoardHistory);
    setMoveHistory(newMoveHistory);
  }, [boardHistory, moveHistory]);

  /**
   * Handles the undo action requested by the user.
   * Reverts the board state, executed moves, and current move index to the previous state.
   * Clears error feedback and resets hint/recovery states.
   * @returns {boolean} True if the undo operation was successful, false otherwise.
   */
  const handleUndo = useCallback((): boolean => {
    if (challengeStatus !== 'in-progress' || currentMoveIndex === 0) {
      announceToScreenReader('No moves to undo');
      return false;
    }

    try {
      // Get the previous board state
      const previousFen = boardHistory[currentMoveIndex - 1];
      const previousMoves = moveHistory.slice(0, currentMoveIndex - 1);
      
      if (!previousFen) {
        console.error('No previous board state available for undo');
        return false;
      }

      // Update the chess service to the previous state
      chessService.resetBoard(previousFen);
      
      // Update component state
      setCurrentFen(previousFen);
      setExecutedMoves(previousMoves);
      setCurrentMoveIndex(currentMoveIndex - 1);
      
      // Clear any error feedback and recovery state
      setErrorFeedback(null);
      setHintSquares(null);
      setShowRecoveryOptions(false);
      setRecoveryHintSquares(null);
      // Force board instance reset so stale arrows/selection cannot persist.
      setBoardResetNonce(prev => prev + 1);
      setUndoCount(prev => prev + 1);
      
      console.log(`Undo successful: reverted to move ${currentMoveIndex - 1}`);
      announceToScreenReader('Move undone');
      return true;
    } catch (error) {
      console.error('Error during undo operation:', error);
      setErrorFeedback('Unable to undo move. System error detected.');
      return false;
    }
  }, [challengeStatus, currentMoveIndex, boardHistory, moveHistory, chessService]);

  /**
   * Handles the action to show a hint for the next correct move as part of the recovery path.
   * Sets hint squares for visual feedback and marks that a hint has been used.
   * Clears any previous error messages.
   */
  const handleShowHint = useCallback(() => {
    if (challengeStatus !== 'in-progress' || currentMoveIndex >= sequence.moves.length) {
      announceToScreenReader('Cannot show hint at this time.');
      return;
    }

    const expectedMoveSAN = sequence.moves[currentMoveIndex];
    const tempGameForHint = new Chess(currentFen);
    
    try {
      const moveDetails = tempGameForHint.move(expectedMoveSAN);
      if (moveDetails) {
        setRecoveryHintSquares({ from: moveDetails.from, to: moveDetails.to });
        setHintCount(prev => prev + 1);
        setShowRecoveryOptions(false);
        setErrorFeedback(null); // Clear error when showing hint
        console.log(`Hint shown: ${moveDetails.from} to ${moveDetails.to} for move ${expectedMoveSAN}`);
        announceToScreenReader('Hint displayed.');
      } else {
        console.error('Could not parse expected move for hint:', expectedMoveSAN);
        announceToScreenReader('Hint not available for the current move.');
      }
    } catch (error) {
      console.error('Error generating hint:', error);
      announceToScreenReader('Error generating hint.');
    }
  }, [challengeStatus, currentMoveIndex, sequence.moves, currentFen]);

  /**
   * Handles the action to automatically execute the next correct move as part of the recovery path.
   * Updates the board state and progresses the challenge.
   * Announces the move to screen readers for accessibility.
   */
  const handleShowCorrectMove = useCallback(() => {
    if (challengeStatus !== 'in-progress' || currentMoveIndex >= sequence.moves.length) {
      return;
    }

    const expectedMoveSAN = sequence.moves[currentMoveIndex];
    
    try {
      // Execute the correct move
      chessService.setFen(currentFen);
      const moveResult = chessService.makeSanMove(expectedMoveSAN);
      
      if (moveResult) {
        const newFen = chessService.getFen();
        const newExecutedMoves = [...executedMoves, expectedMoveSAN];
        const newCurrentMoveIndex = currentMoveIndex + 1;
        
        // Update state with history tracking
        updateStateWithHistory(newFen, newExecutedMoves, newCurrentMoveIndex);
        
        // Clear recovery state
        setShowRecoveryOptions(false);
        setErrorFeedback(null);
        setRecoveryHintSquares(null);
        const nextAutoMoveCount = autoMoveCount + 1;
        setAutoMoveCount(nextAutoMoveCount);
        
        console.log(`Correct move executed automatically: ${expectedMoveSAN}`);
        
        // Check if sequence is complete
        if (newCurrentMoveIndex === sequence.moves.length) {
          setChallengeStatus('completed');
          const timeSpent = startTime ? (Date.now() - startTime) / 1000 : 0;
          const score = calculateFinalScore({ autoMoveCount: nextAutoMoveCount });
          onComplete({ success: true, score, moves: newExecutedMoves, timeSpent });
        }
      } else {
        console.error('Failed to execute correct move:', expectedMoveSAN);
        setErrorFeedback('Unable to execute the correct move. System error detected.');
      }
    } catch (error) {
      console.error('Error executing correct move:', error);
      setErrorFeedback('Unable to execute the correct move. System error detected.');
    }
  }, [challengeStatus, currentMoveIndex, sequence.moves, currentFen, chessService, executedMoves, updateStateWithHistory, startTime, onComplete, autoMoveCount, calculateFinalScore]);

  /**
   * Effect to set up keyboard navigation and shortcuts for the challenge container.
   * Uses the `handleKeyboardShortcuts` utility to manage undo, hint, and other custom shortcuts.
   * Cleans up event listeners on unmount or when dependencies change.
   */
  useEffect(() => {
    const element = containerRef.current;
    if (!element || !enableKeyboardNavigation) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcutsMap: Record<string, () => void> = {
        // Standard shortcuts
        'u': () => { 
          if (handleUndo()) {
            onKeyboardShortcut?.('undo');
          }
        },
        'h': () => {
          if (showRecoveryOptions) { // Only trigger hint from recovery via H key for now
            handleShowHint(); 
            onKeyboardShortcut?.('hint'); 
          } else if (showHints) { // If general hints are on and no recovery
            // Potentially call a general hint display function if different from recovery hint
            // For now, let's assume 'h' primarily works when recovery is up or if general hints are on
            // and the ChessBoard itself might handle general hint display via its own keyboard nav.
            // If SequenceChallenge is to manage all 'h' key for hints:
            // handleShowGeneralHint(); // a new function might be needed
            onKeyboardShortcut?.('hint'); // Or just call this if 'h' is for any hint
            // announceToScreenReader('Hint displayed.'); // Example
          } else {
            announceToScreenReader('Hints are disabled for this challenge.');
            onKeyboardShortcut?.('hint_disabled');
          }
        },
        // Ctrl+R for recovery options
        'r': () => { 
          if (event.ctrlKey || event.metaKey) { // Check for Ctrl or Cmd key
            setShowRecoveryOptions(prev => !prev); 
            onKeyboardShortcut?.('toggleRecovery');
          }
        },
        // Escape for closing recovery options or clearing selection (if no dialogs are managed here)
        'Escape': () => {
          if (showRecoveryOptions) {
            setShowRecoveryOptions(false);
            setErrorFeedback(null); // Clear error when closing recovery
            setRecoveryHintSquares(null); // Clear recovery hint when closing
            onKeyboardShortcut?.('closeRecovery');
            announceToScreenReader('Recovery options closed.');
          } else {
            // If other escape functionalities are needed (e.g., clearing board selection),
            // they should be handled here or in the ChessBoard component's keyboard handling.
            onKeyboardShortcut?.('escape');
          }
        },
        // Enter key for recovery confirmation (show correct move)
        'Enter': () => {
          if (showRecoveryOptions) {
            handleShowCorrectMove();
            onKeyboardShortcut?.('submit_recovery');
            announceToScreenReader('Correct move shown.');
          }
          // Potentially other Enter key functionalities if not in recovery mode
        }
      };

      // Check if the pressed key (potentially with modifiers) matches a defined shortcut
      let shortcutKey = event.key.toLowerCase();
      if ((event.ctrlKey || event.metaKey) && shortcutKey !== 'r') { // Avoid double prefix for Ctrl+R
        shortcutKey = `ctrl+${shortcutKey}`;
      } 
      // Add other modifier checks (alt, shift) if needed

      if (handleKeyboardShortcuts(event, shortcutsMap)) {
        // The utility already calls preventDefault if a shortcut is matched and executed.
        // onKeyboardShortcut is called within the lambdas above for more specific feedback.
      }
    };

    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableKeyboardNavigation, handleUndo, handleShowHint, onKeyboardShortcut, containerRef, showRecoveryOptions, setShowRecoveryOptions, handleShowCorrectMove, showHints]);

  /**
   * Handles a move made by the player on the ChessBoard.
   * Validates the move against the expected move in the sequence using `chessService.validateMoveInSequence`.
   * If correct, updates the game state, FEN, executed moves, and current move index.
   * If incorrect, provides error feedback and shows recovery options.
   * Handles challenge completion (both success and failure scenarios).
   * @param from The starting square of the player's move (e.g., "e2").
   * @param to The destination square of the player's move (e.g., "e4").
   * @param promotion Optional piece type for pawn promotion.
   */
  const handlePlayerMove = useCallback((from: string, to: string, promotion?: PieceType) => {
    if (challengeStatus !== 'in-progress') return;

    setErrorFeedback(null);
    setHintSquares(null);

    const tempGame = new Chess(currentFen);
    const moveAttempt = tempGame.move({ from, to, promotion });

    if (!moveAttempt) {
      setErrorFeedback('Invalid maneuver. Re-evaluate your approach.');
      setWrongMoveCount(prev => prev + 1);
      setBoardResetNonce(prev => prev + 1);
      return;
    }

    const playerMoveSAN = moveAttempt.san;
    const expectedMoveSAN = sequence.moves[currentMoveIndex];
    
    console.log('Player move:', playerMoveSAN, 'Expected move:', expectedMoveSAN);
    
    // More flexible move validation - normalize both moves for comparison
    // Check if the moves are equivalent (e.g., 'Bb5' vs 'B-b5' vs 'Bf1-b5')
    const isMoveCorrect = chessService.validateMoveInSequence(currentFen, playerMoveSAN, expectedMoveSAN);

    if (isMoveCorrect) {
      try {
        // Normalize the player's move by removing check/checkmate symbols before execution
        const normalizedPlayerMove = playerMoveSAN.replace(/[+#]/g, '');
        
        // Use the normalized player move for execution
        console.log('Move is correct. Attempting to execute:', normalizedPlayerMove);
        chessService.setFen(currentFen);
        const officialMoveResult = chessService.makeSanMove(normalizedPlayerMove);
        
        if (officialMoveResult) {
          console.log('Move executed successfully:', officialMoveResult);
          const newFen = chessService.getFen();
          // Use the SAN from the officialMoveResult, as this is what the service processed.
          const newExecutedMoves = [...executedMoves, officialMoveResult.san]; 
          const newCurrentMoveIndex = currentMoveIndex + 1;
          
          // Update state with history tracking
          updateStateWithHistory(newFen, newExecutedMoves, newCurrentMoveIndex);
          
          // Clear recovery state on successful move
          setShowRecoveryOptions(false);
          setRecoveryHintSquares(null);

          if (newCurrentMoveIndex === sequence.moves.length) {
            setChallengeStatus('completed');
            const timeSpent = startTime ? (Date.now() - startTime) / 1000 : 0;
            const score = calculateFinalScore();
            onComplete({ success: true, score, moves: newExecutedMoves, timeSpent });
          }
        }
        else {
          console.error('Move validation succeeded but application failed:', normalizedPlayerMove, 'on FEN:', currentFen);
          // Try with the expected move as a fallback
          console.log('Trying fallback with expected move:', expectedMoveSAN);
          const normalizedExpectedMove = expectedMoveSAN.replace(/[+#]/g, '');
          const fallbackMoveResult = chessService.makeSanMove(normalizedExpectedMove);
          
          if (fallbackMoveResult) {
            console.log('Fallback move succeeded:', fallbackMoveResult);
            const newFen = chessService.getFen();
            // Use the SAN from the fallbackMoveResult
            const newExecutedMoves = [...executedMoves, fallbackMoveResult.san];
            const newCurrentMoveIndex = currentMoveIndex + 1;
            
            // Update state with history tracking
            updateStateWithHistory(newFen, newExecutedMoves, newCurrentMoveIndex);
            
            // Clear recovery state on successful move
            setShowRecoveryOptions(false);
            setRecoveryHintSquares(null);

            if (newCurrentMoveIndex === sequence.moves.length) {
              setChallengeStatus('completed');
              const timeSpent = startTime ? (Date.now() - startTime) / 1000 : 0;
              const score = calculateFinalScore();
              onComplete({ success: true, score, moves: newExecutedMoves, timeSpent });
            }
          } else {
            // Last resort: Try to use the from-to approach directly
            console.log('Both SAN notations failed, trying direct coordinates approach');
            
            // Parse the expected move to determine piece and target
            // For bishop moves like Bb5, we need to find a bishop that can move to b5
            const isPieceMove = /^([KQRBNP])([a-h][1-8])/.test(expectedMoveSAN);
            const targetSquare = expectedMoveSAN.match(/[a-h][1-8]/)?.[0];
            
            if (isPieceMove && targetSquare) {
              // Get the piece type from the move notation (B for bishop, etc.)
              const pieceType = expectedMoveSAN[0].toLowerCase() as PieceType;
              
              // Find all pieces of this type
              const pieces = chessService.getPosition()
                .filter(p => p.type === pieceType && p.color === 'w');
         
              // Try to find a piece that can legally move to the target square
              for (const piece of pieces) {
                const legalMoves = chessService.getLegalMoves(piece.square);
                if (legalMoves.includes(targetSquare)) {
                  // Found a piece that can make this move
                  console.log(`Found ${pieceType} at ${piece.square} that can move to ${targetSquare}`);
                  const directMoveResult = chessService.makeMove(piece.square, targetSquare);
                  
                  if (directMoveResult) {
                    console.log('Direct move succeeded:', directMoveResult);
                    const newFen = chessService.getFen();
                    // Use the SAN from the directMoveResult (which is a ChessJsMove, so directMoveResult.san)
                    const newExecutedMoves = [...executedMoves, directMoveResult.san];
                    const newCurrentMoveIndex = currentMoveIndex + 1;
                    
                    // Update state with history tracking
                    updateStateWithHistory(newFen, newExecutedMoves, newCurrentMoveIndex);
                    
                    // Clear recovery state on successful move
                    setShowRecoveryOptions(false);
                    setRecoveryHintSquares(null);
                    
                    if (newCurrentMoveIndex === sequence.moves.length) {
                      setChallengeStatus('completed');
                      const timeSpent = startTime ? (Date.now() - startTime) / 1000 : 0;
                      const score = calculateFinalScore();
                      onComplete({ success: true, score, moves: newExecutedMoves, timeSpent });
                    }
                    break;
                  }
                }
              }
            }
            
            // If we get here, all approaches failed
            setErrorFeedback('System anomaly detected with move execution. Our technicians will investigate.');
            console.error('All move execution approaches failed:', playerMoveSAN, expectedMoveSAN, 'on FEN:', currentFen);
          }
        }
      } catch (error) {
        console.error('Exception in move application:', error);
        setErrorFeedback('System anomaly detected with move execution. Our technicians will investigate.');
      }
    } else {
      setErrorFeedback(`Target missed. Expected: ${expectedMoveSAN}. Your move: ${playerMoveSAN}`);
      setShowRecoveryOptions(true); // Show recovery options after incorrect move
      setRecoveryHintSquares(null); // Clear any existing hint squares
      setWrongMoveCount(prev => prev + 1);
      // Reset board-local visual state (selection/arrow) after rejected move.
      setBoardResetNonce(prev => prev + 1);
    }
  }, [challengeStatus, currentFen, currentMoveIndex, sequence, onComplete, chessService, executedMoves, startTime, updateStateWithHistory, calculateFinalScore]);

  /**
   * Callback for the Timer component to update the time left in the state.
   * @param remainingTime The time remaining in seconds.
   */
  const handleTimeUpdate = useCallback((remainingTime: number) => {
    setTimeLeft(remainingTime);
  }, []);

  /**
   * Callback for the Timer component when the time limit is reached.
   * Sets the challenge status to 'failed' and calls the onComplete prop with the failure result.
   */
  const handleTimeUp = useCallback(() => {
    if (challengeStatus !== 'in-progress') return;
    setChallengeStatus('failed');
    const timeSpent = startTime ? (Date.now() - startTime) / 1000 : timeLimit || 0;
    onComplete({ success: false, score: 0, moves: executedMoves, timeSpent });
  }, [challengeStatus, onComplete, executedMoves, startTime, timeLimit]);

  /**
   * Callback for the SequenceTracker component's onComplete prop.
   * Note: The primary challenge completion logic is handled within `handlePlayerMove` or `handleTimeUp`.
   * This callback mainly serves the SequenceTracker's internal state management if needed.
   * @param success Indicates whether the sequence was completed successfully according to the tracker.
   */
  const handleSequenceTrackerComplete = useCallback((success: boolean) => {
    // This callback is primarily for the SequenceTracker's internal logic.
    // The overall challenge completion is handled by handlePlayerMove or handleTimeUp.
    // console.log(`SequenceTracker reported completion: ${success}`);
  }, []);

  return (
    <div 
      ref={containerRef}
      className={styles.container}
      tabIndex={enableKeyboardNavigation ? 0 : -1}
      role="application"
      aria-label="Sequence Challenge"
      aria-describedby="sequence-instructions"
    >
      <div 
        id="sequence-instructions" 
        className={styles.srOnly}
        aria-live="polite"
      >
        {enableKeyboardNavigation && (
          <>
            Use arrow keys to navigate the chess board. 
            Press U to undo, H for hint when available, Enter to submit, Escape to close dialogs.
          </>
        )}
      </div>
      <div className={styles.gameArea}>
        {sequence.tacticalTheme && (
          <div className={styles.tacticalContextContainer}>
            <TacticalContext
              scenarioDescription={sequence.description || `Execute the sequence: ${sequence.moves.join(', ')}`}
              objectiveType={mapTacticalThemeToObjectiveType(sequence.tacticalTheme)}
              tacticalHint={tacticalHintText}
            />
          </div>
        )}
        <div className={styles.chessBoardContainer}>
          <ChessBoard
            key={`sequence-board-${currentFen}-${boardResetNonce}`}
            fen={currentFen}
            onMove={handlePlayerMove}
            allowMoves={challengeStatus === 'in-progress'}
            highlightLastMove={true}
            highlightSquare={
              (showHints && hintSquares ? hintSquares.from : undefined) ||
              (recoveryHintSquares ? recoveryHintSquares.from : undefined)
            }
            chessService={chessService}
            enableKeyboardNavigation={enableKeyboardNavigation}
            onKeyboardShortcut={onKeyboardShortcut}
          />
        </div>
        <div className={styles.infoArea}>
          <SequenceTracker
            moveSequence={sequence.moves}
            currentMoveIndex={currentMoveIndex}
            onComplete={handleSequenceTrackerComplete}
            onUndo={handleUndo}
            canUndo={currentMoveIndex > 0 && challengeStatus === 'in-progress'}
            maxUndos={3}
          />
          <ProgressVisualizer
            totalMoves={sequence.moves.length}
            completedMoves={executedMoves.length}
            variant="steps"
          />
          {timeLimit && timeLeft !== null && (
            <Timer
              duration={timeLimit}
              onTick={handleTimeUpdate}
              onComplete={handleTimeUp}
              active={challengeStatus === 'in-progress'}
              key={sequence.id}
            />
          )}
          {errorFeedback && (
            <div 
              className={styles.errorFeedback}
              role="alert"
              aria-live="assertive"
              tabIndex={-1}
            >
              {errorFeedback}
            </div>
          )}
          
          {/* Recovery hint display */}
          {recoveryHintSquares && (
            <div className={styles.hintDisplay} data-testid="hint-display">
              <p className={styles.hintText}>
                💡 <strong>Hint:</strong> Move from {recoveryHintSquares.from} to {recoveryHintSquares.to}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Debug information panel */}
      <div className={styles.debugInfo} data-testid="debug-info-container">
        <h4>Debug Info:</h4>
        <p><strong>Sequence ID:</strong> {sequence.id}</p>
        <p><strong>Status:</strong> {challengeStatus}</p>
        <p><strong>FEN:</strong> {currentFen}</p>
        <p><strong>Executed Moves:</strong> {executedMoves.length > 0 
          ? executedMoves.map((move, i) => `${i+1}. ${move}`).join(", ") 
          : "None"}</p>
        <p><strong>Current Move Index:</strong> {currentMoveIndex}</p>
        <p><strong>Expected Move:</strong> {currentMoveIndex < sequence.moves.length 
          ? sequence.moves[currentMoveIndex] 
          : "Sequence Complete"}</p>
        <p><strong>Time Left:</strong> {timeLeft !== null ? Math.round(timeLeft) : "No time limit"}</p>
        {errorFeedback && <p><strong>Error:</strong> {errorFeedback}</p>}
        {hintSquares && <p><strong>Hint:</strong> Move from {hintSquares.from} to {hintSquares.to}</p>}
        <p><strong>Recovery Options:</strong> {showRecoveryOptions ? "Shown" : "Hidden"}</p>
        <p><strong>Penalty Counters:</strong> wrong={wrongMoveCount}, hints={hintCount}, auto={autoMoveCount}, undo={undoCount}</p>
        {recoveryHintSquares && <p><strong>Recovery Hint:</strong> Move from {recoveryHintSquares.from} to {recoveryHintSquares.to}</p>}
      </div>

      {challengeStatus === 'in-progress' && (
        <div className={styles.controlsContainer} role="toolbar" aria-label="Challenge controls">
          <button 
            onClick={handleUndo} 
            disabled={executedMoves.length === 0}
            className={styles.controlButton}
            aria-label="Undo last move (U or Ctrl+Z)"
            tabIndex={0}
          >
            Undo ({executedMoves.length})
          </button>
          {showHints && (
            <button 
              onClick={handleShowHint} 
              className={styles.controlButton}
              aria-label="Show Hint (H or F1)"
              tabIndex={0}
            >
              Show Hint
            </button>
          )}
        </div>
      )}

      {/* Consolidated Recovery Options Container */}
      {showRecoveryOptions && (
        <div className={styles.recoveryOptionsContainer} data-testid="recovery-options-container">
          <h4 id="recovery-title" className={styles.recoveryTitle}>That wasn't the correct move. What would you like to do?</h4>
          <button 
            onClick={handleShowHint} 
            className={styles.recoveryButton}
            data-testid="recovery-show-hint-button"
            aria-label="Show a hint for the correct move (H or F1)"
          >
            Show Hint (Penalty)
          </button>
          <button 
            onClick={handleShowCorrectMove} 
            className={styles.recoveryButton}
            data-testid="recovery-show-correct-move-button"
            aria-label="Show the correct move (Enter)"
          >
            Show Correct Move
          </button>
          <button 
            onClick={() => {
              setShowRecoveryOptions(false);
              setErrorFeedback(null);
              setRecoveryHintSquares(null);
            }}
            className={styles.recoveryButton}
            data-testid="recovery-try-again-button"
            aria-label="Try again without hints (Escape)"
          >
            Try Again
          </button>
          <p className={styles.recoveryNote}>
            Using hints will affect your final score.
          </p>
        </div>
      )}

      {challengeStatus !== 'in-progress' && (
        <div className={styles.controlsContainer}>
          <button 
            onClick={() => {
              // Implement the logic to restart the challenge
              // This might involve resetting the game state
              // and possibly calling the onComplete callback with a new sequence
              // or simply reloading the component
            }}
            className={styles.controlButton}
            aria-label="Restart Challenge"
          >
            Restart Challenge
          </button>
        </div>
      )}
    </div>
  );
};

export default SequenceChallenge; 
